export interface ReminderRecord {
    id: number;
    title: string;
    start_time: string;
    end_time: string;
    sequence: number;
    // Client-supplied, not echoed by the backend response — the course-taking URL to link
    // back to from the exported .ics event, carried over from the enrollment that triggered
    // this reminder. Already an absolute URL (the GraphQL `URL` scalar it's sourced from
    // always is) — do not run this through udLink.makeAbsolute() or similar, that double-
    // prefixes the domain.
    courseUrl?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

export function toMonolithTime(d: Date): string {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

export function getReminderTimes(daysOffset: number, hour: number): {start: Date; end: Date} {
    const now = new Date();
    const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + daysOffset,
        hour,
        0,
        0,
    );
    const end = new Date(start.getTime() + 30 * 60 * 1000);
    return {start, end};
}

function formatTZOffset(offsetMinutes: number): string {
    const sign = offsetMinutes < 0 ? '-' : '+';
    const hours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(2, '0');
    const minutes = String(Math.abs(offsetMinutes) % 60).padStart(2, '0');
    return `${sign}${hours}${minutes}`;
}

function getTZName(date: Date): string {
    const part = new Intl.DateTimeFormat('en-US', {timeZoneName: 'short'})
        .formatToParts(date)
        .find((p) => p.type === 'timeZoneName');
    return part?.value ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Minimal VTIMEZONE block (RFC 5545 §3.6.5) so calendar clients correctly interpret a
 * named TZID. A single STANDARD section carrying the offset active at `referenceDate`
 * is sufficient here — our reminders are always one-shot (never RRULE-recurring), so
 * there's no DST transition within the event's own lifetime to model, unlike a
 * recurring-event VTIMEZONE which needs STANDARD/DAYLIGHT sections to switch across
 * occurrences.
 */
function generateVTimezone(referenceDate: Date): string {
    const tzId = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offset = formatTZOffset(-referenceDate.getTimezoneOffset());
    const tzName = getTZName(referenceDate);
    return [
        'BEGIN:VTIMEZONE',
        `TZID:${tzId}`,
        'BEGIN:STANDARD',
        'DTSTART:19700101T000000',
        `TZOFFSETFROM:${offset}`,
        `TZOFFSETTO:${offset}`,
        `TZNAME:${tzName}`,
        'END:STANDARD',
        'END:VTIMEZONE',
    ].join('\r\n');
}

export function generateIcs(reminder: ReminderRecord): string {
    const tzId = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dtstamp =
        new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/[^a-z0-9]/gi, '') + 'Z';
    const dtstart = reminder.start_time.replace(/[^a-z0-9]/gi, '');
    const dtend = reminder.end_time.replace(/[^a-z0-9]/gi, '');
    return [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//udemy.com//Udemy',
        'METHOD:PUBLISH',
        generateVTimezone(new Date(reminder.start_time)),
        'BEGIN:VEVENT',
        `ORGANIZER;CN="Udemy":mailto:no-reply@udemy.com`,
        `SUMMARY:${reminder.title}`,
        `SEQUENCE:${reminder.sequence}`,
        `DTSTAMP:${dtstamp}`,
        `DTSTART;TZID=${tzId}:${dtstart}`,
        `DTEND;TZID=${tzId}:${dtend}`,
        `UID:${reminder.id}`,
        ...(reminder.courseUrl ? [`DESCRIPTION:${reminder.title} (${reminder.courseUrl})`] : []),
        'END:VEVENT',
        'END:VCALENDAR',
    ].join('\r\n');
}

export function triggerIcsDownload(reminder: ReminderRecord): void {
    const element = document.createElement('a');
    element.setAttribute(
        'href',
        `data:text/calendar;charset=utf-8,${encodeURIComponent(generateIcs(reminder))}`,
    );
    element.setAttribute('download', 'learning-reminder.ics');
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
}
